import Lottie from "lottie-react";
import moment from "moment";
import queryString from "query-string";
import { useEffect, useMemo, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { IoIosWarning } from "react-icons/io";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import * as contactLoading from "../animations/profile.json";
import medFragIcon from "../assets/MedFrag.png";
import {
  getFieldDate,
  getFieldName,
  getFieldText,
} from "../scripts/helperScripts";
import Assessments from "./Assessments";
import CareTeamEntity from "./CareTeamEntity";
import ClientInfo from "./ClientInfo";
import DiagnosisEntity from "./DiagnosisEntity";
import DocumentationTimeline from "./DocumentationTimeline";
import BackIcon from "./common/BackIcon";
import BedDaysTab from "./common/BedDaysTab";
import CarePlanButton from "./common/CarePlanButton";
import EncounterButton from "./common/EncounterButton";
import FormsTab from "./common/FormsTab";
import IncidentReportButton from "./common/IncidentReportButton";
import ProgramRadioSelect from "./common/ProgramRadioSelect";
import RecordedServiceButton from "./common/RecordedServiceButton";
import getAge from "./services/ageService";
import {
  crisisReferrals,
  getAddresses,
  getAllergies,
  getBedAppointments,
  getCareModalities,
  getCareTeam,
  getClientCarePlans,
  getClientDiagnosisDetails,
  getClientIdentifiers,
  getClientPayors,
  getClientPhoto,
  getClientProgramIdentifiers,
  getClientPrograms,
  getClientRates,
  getContact,
  getEncounters,
  getFormsySubmissionsForMultiple,
  getFosterBeds,
  getIncidents,
  getMedications,
  getPermanencyStatuses,
  getProgressNotes,
  getRecordedServices,
  getReferralsByClient,
  getServiceItems,
  getSoarCorrespondence,
  sendEmail,
} from "./services/soar";
import Documents from "./sharepoint/Documents";
import { DateTime } from "luxon";

const Client2Wrapper = () => {
  const { clientId } = useParams();
  return <Client2 key={clientId} />;
};

const Client2 = () => {
  const location = useLocation();
  const qs = queryString.parse(location.search);
  const initialTab = useMemo(
    () => location.state?.tab || qs.tab,
    [location.state?.tab, qs.tab]
  );
  const debugMode = useMemo(() => {
    return qs.debug === "true";
  }, [qs.debug]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [programsLoaded, setProgramsLoaded] = useState(false);
  const [currentCarePlans, setCurrentCarePlans] = useState([]);
  const [selected, setSelected] = useState();
  const { clientId } = useParams();

  const [contact, setContact] = useState({});
  const [contactLoaded, setContactLoaded] = useState(false);
  const [clientPhoto, setClientPhoto] = useState(null);
  const [diagnosis, setDiagnosis] = useState([]);
  const [updateDiagnosis, setUpdateDiagnosis] = useState(false);
  const [careTeam, setCareTeam] = useState([]);
  const [careTeamLoaded, setCareTeamLoaded] = useState(false);
  const [updateCareTeam, setUpdateCareTeam] = useState(false);
  const [carePlans, setCarePlans] = useState([]);
  const [updateCarePlans, setUpdateCarePlans] = useState(false);
  const [recordedServices, setRecordedServices] = useState([]);
  const [progressNotes, setProgressNotes] = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [correspondence, setCorrespondence] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [clientPayors, setClientPayors] = useState([]);
  const [updateClientPayors, setUpdateClientPayors] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [updateAddresses, setUpdateAddresses] = useState(false);
  const [beds, setBeds] = useState([]);
  const [updateBeds, setUpdateBeds] = useState(false);
  const [modalities, setModalities] = useState([]);
  const [updateModalities, setUpdateModalities] = useState(false);
  const [rates, setRates] = useState([]);
  const [updateRates, setUpdateRates] = useState(false);
  const [allergies, setAllergies] = useState([]);
  const [updateAllergies, setUpdateAllergies] = useState(false);
  const [medications, setMedications] = useState([]);
  const [updateMedications, setUpdateMedications] = useState(false);
  const [permanencyStatuses, setPermanencyStatuses] = useState([]);
  const [updatePermanencyStatuses, setUpdatePermanencyStatuses] =
    useState(false);
  const [bedAppointments, setBedAppointments] = useState([]);
  const [updateBedAppointments, setUpdateBedAppointments] = useState(false);
  const [clientIdentifiers, setClientIdentifiers] = useState([]);
  const [updateClientIdentifiers, setUpdateClientIdentifiers] = useState(false);
  const [clientProgramIdentifiers, setClientProgramIdentifiers] = useState([]);
  const [fosterHomePrograms, setFosterHomePrograms] = useState([]);
  const [excludedServiceItemIds, setExcludedServiceItemIds] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const isMountedRef = useRef(true);
  const [showExpired, setShowExpired] = useState(false);

  const [timeline, setTimeline] = useState([]);
  const [timelineLoaded, setTimelineLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("client");
  const [formHistory, setFormHistory] = useState([]);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  //Route Access Protection
  useEffect(() => {
    if (safyInfo) {
      const modules = safyInfo.user.modules;
      if (
        modules.includes("My Caseload") === false &&
        modules.includes("Team Caseload") === false
      ) {
        navigate("/");
      }
    } else navigate("/");
  });

  useEffect(() => {
    return () => {
      document.title = "SAFY Portal";
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
      if (initialTab === "clinicalHistory") loadTimeline();
    }
  }, [initialTab, location.state?.clientId]);

  //Get Care Team
  useEffect(() => {
    if (clientId || updateCareTeam) {
      setCareTeamLoaded(false);
      getCareTeam(clientId).then((response) => {
        if (isMountedRef.current) {
          if (response) {
            const contacts = response.filter(
              (collateral) =>
                getFieldText(collateral.ccx_membertype) === "803080001"
            );
            contacts.sort((a, b) => {
              if (a.ccx_name > b.ccx_name) return 1;
              else if (a.ccx_name < b.ccx_name) return -1;
              return 0;
            });
            const providers = response.filter(
              (collateral) =>
                getFieldText(collateral.ccx_membertype) === "803080002"
            );
            providers.sort((a, b) => {
              if (a.ccx_name > b.ccx_name) return 1;
              else if (a.ccx_name < b.ccx_name) return -1;
              return 0;
            });

            setCareTeam([...contacts, ...providers]);
          }

          setCareTeamLoaded(true);
        }
      });
      setUpdateCareTeam(false);
    } else {
      setCareTeam([]);
    }
  }, [clientId, updateCareTeam]);

  const handleUpdateCareTeam = () => {
    setUpdateCareTeam(true);
  };

  useEffect(() => {
    if (safyInfo && clientId && careTeamLoaded) {
      getClientPrograms(clientId).then((response) => {
        if (isMountedRef.current) {
          if (response) {
            setAllPrograms(response);

            console.group("ProgramFiltering");
            const programsForCareTeamMember = response.filter((program) => {
              if (!program.ccx_name.includes("Foster Homes")) {
                if (safyInfo.user.modules.includes("Team Caseload")) {
                  return true;
                } else {
                  const amMemberOf = careTeam.filter((ct) => {
                    if (
                      getFieldText(ct.ccx_clientprogram) ===
                      program.ccx_client_programid
                    ) {
                      if (
                        getFieldText(ct.ccx_memberid) ===
                        `{${safyInfo.user.safyInfo.SOARUserId}}`
                      ) {
                        return true;
                      }
                    }

                    return false;
                  });
                  if (amMemberOf.length > 0) return true;
                }
              }

              return false;
            });
            console.groupEnd();

            setPrograms(programsForCareTeamMember);

            let firstActiveProgram = programsForCareTeamMember.filter(
              (program) => program.ccx_dischargedate === undefined
            )[0];
            if (firstActiveProgram === undefined) {
              firstActiveProgram = programsForCareTeamMember[0];
              setShowExpired(true);
            }

            if (firstActiveProgram !== undefined) {
              setSelected(firstActiveProgram);
              loadClientProgramIdentifiers(
                firstActiveProgram.ccx_client_programid
              );
            }

            const fosterPrograms = response.filter((program) =>
              program.ccx_name.includes("Foster")
            );
            setFosterHomePrograms(fosterPrograms);
          }
        }
      });
    } else {
      setFosterHomePrograms([]);
    }
  }, [clientId, careTeamLoaded]);

  useEffect(() => {
    if (clientId) {
      getFormsySubmissionsForMultiple([clientId]).then((response) => {
        if (response && isMountedRef.current) {
          setFormHistory(response);
        }
      });
    }
  }, [clientId]);

  // useEffect(() => {
  //   if (selected) {
  //     getCarePlan(selected).then((response) => {
  //       if (response && isMountedRef.current) setCurrentCarePlans(response);
  //     });
  //   }
  // }, [selected]);

  // load excluded service items
  useEffect(() => {
    if (programsLoaded) {
      getServiceItems().then((response) => {
        if (isMountedRef.current && response) {
          const excluded = response
            .filter(
              (serviceItem) => getFieldText(serviceItem.ccx_needs_bed) === "1"
            )
            .map((serviceItem) => serviceItem.ccx_service_itemid);

          setExcludedServiceItemIds(excluded);
        }
      });
    }
  }, [programsLoaded]);

  useEffect(() => {
    getClientPhoto(clientId).then((response) => {
      if (isMountedRef.current && response && response.src) {
        setClientPhoto(response.src);
      }
    });
  }, [clientId]);

  const addToTimeline = (
    records,
    type,
    id,
    name,
    effdate,
    expdate,
    createdby,
    createdon,
    clientprogram
  ) => {
    const newData = records.map((item) => {
      const obj = {
        type: type,
        id: item[id],
        name: item[name],
        effdate: item[effdate] ? item[effdate] : item[createdon],
        expdate: item[expdate],
        createdby: item[createdby],
        createdon: item[createdon],
        clientprogram: item[clientprogram],
      };
      return obj;
    });
    setTimeline((prevData) => [...prevData, ...newData]);
  };

  //get Client Info
  useEffect(() => {
    if (!clientId) {
      return;
    }

    const loadContact = async () => {
      const response = await getContact(clientId);

      if (!isMountedRef.current) {
        return;
      }

      if (!response?.[0]) {
        // TODO: error log
        setContactLoaded(true);
        return;
      }

      setContact(response[0]);
      document.title = `SAFY Portal - ${response[0].ccx_mpistr}`;
      setContactLoaded(true);
    };

    loadContact();
  }, [clientId]);

  //Filter Diagnosis to selected Program
  useEffect(() => {
    if (
      selected !== undefined &&
      (selected.ccx_client_programid || updateDiagnosis === true) &&
      programsLoaded
    ) {
      getClientDiagnosisDetails(selected["con.contactid"]).then((response) => {
        if (response && isMountedRef.current) {
          setDiagnosis(response);
          // addToTimeline(
          //   response,
          //   "Diagnosis",
          //   "ccx_clientdiagnosisdetailid",
          //   "ccx_name",
          //   "ccx_effdate",
          //   "ccx_expdate",
          //   "createdby",
          //   "createdon"
          // );
        }
      });
      setUpdateDiagnosis(false);
    } else {
      setDiagnosis([]);
    }
  }, [selected, updateDiagnosis, programsLoaded]);

  const handleUpdateDiagnosis = () => {
    setUpdateDiagnosis(true);
  };

  //Addresses
  useEffect(() => {
    if (
      (addresses.length > 0 && !updateAddresses) ||
      !clientId ||
      !programsLoaded
    ) {
      return;
    }

    const loadAddresses = async () => {
      const response = await getAddresses(clientId);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAddresses(response);
      // addToTimeline(
      //   response,
      //   "Address",
      //   "ccx_contactaddressid",
      //   "ccx_name",
      //   "ccx_effectivedate",
      //   "ccx_expirationdate",
      //   "createdby",
      //   "createdon"
      // );
    };

    loadAddresses();
    setUpdateAddresses(false);
  }, [clientId, updateAddresses, programsLoaded]);

  const handleUpdateAddresses = (doUpdate = true) => {
    setUpdateAddresses(doUpdate);
  };

  //FosterBeds
  useEffect(() => {
    if (clientId && programsLoaded) {
      getFosterBeds(clientId).then((response) => {
        if (response && isMountedRef.current) {
          setBeds(
            response.sort((a, b) => {
              const aDate = moment(getFieldDate(a.ccx_effectivedate));
              const bDate = moment(getFieldDate(b.ccx_effectivedate));

              if (aDate.isBefore(bDate)) {
                return 1;
              }

              if (aDate.isAfter(bDate)) {
                return -1;
              }

              return 0;
            })
          );
          // addToTimeline(
          //   response,
          //   "Foster Beds",
          //   "ccx_fosterchildid",
          //   "ccx_name",
          //   "ccx_actualadmission",
          //   "ccx_actualdischarge",
          //   "createdby",
          //   "createdon"
          // );
        }
      });
    } else {
      setBeds([]);
    }
  }, [clientId, updateBeds, programsLoaded]);

  const handleUpdateBeds = (doUpdate = true) => {
    setUpdateBeds(doUpdate);
  };

  //Care Modalities
  useEffect(() => {
    if ((clientId || updateModalities) && programsLoaded) {
      getCareModalities(clientId).then((response) => {
        if (response && isMountedRef.current) {
          setModalities(response);
          // addToTimeline(
          //   response,
          //   "Care Modalities",
          //   "ccx_caremodalityid",
          //   "ccx_name",
          //   "ccx_startdate",
          //   "ccx_enddate",
          //   "createdby",
          //   "createdon"
          // );
        } else {
        }
      });
      setUpdateModalities(false);
    } else {
      setModalities([]);
    }
  }, [clientId, updateModalities, programsLoaded]);

  const handleUpdateModalities = () => {
    setUpdateModalities(true);
  };

  //Client Rates
  useEffect(() => {
    if ((clientId || updateRates) && programsLoaded) {
      getClientRates(clientId).then((response) => {
        if (response && isMountedRef.current) {
          setRates(response);
          // addToTimeline(
          //   response,
          //   "Client Rates",
          //   "sor_fosterparentreimbursementid",
          //   "sor_name",
          //   "sor_effdate",
          //   "",
          //   "createdby",
          //   "createdon"
          // );
        }
      });
      setUpdateRates(false);
    } else {
      setRates([]);
    }
  }, [clientId, updateRates, programsLoaded]);

  const handleUpdateRates = () => {
    setUpdateRates(true);
  };

  //Allergies
  useEffect(() => {
    if ((clientId || updateAllergies) && programsLoaded) {
      getAllergies(clientId).then((response) => {
        if (response && isMountedRef.current) {
          const sorted = response.sort((a, b) => {
            const aSeverity = getFieldText(a.ccx_severity);
            const aEffectiveDate = new Date(getFieldText(a.ccx_effdate));
            const bSeverity = getFieldText(b.ccx_severity);
            const bEffectiveDate = new Date(getFieldText(b.ccx_effdate));

            if (aSeverity === "803080002") {
              if (bSeverity === "803080003" || bSeverity !== "803080002")
                return -1;
              else {
                if (aEffectiveDate < bEffectiveDate) return 1;
                else if (bEffectiveDate < aEffectiveDate) return -1;
                else return 0;
              }
            } else if (aSeverity === "803080001") {
              if (bSeverity === "803080002") return 1;
              else if (bSeverity === "803080003" || bSeverity === "803080000")
                return -1;
              else {
                if (aEffectiveDate < bEffectiveDate) return 1;
                else if (bEffectiveDate < aEffectiveDate) return -1;
                else return 0;
              }
            } else if (aSeverity === "803080000") {
              if (bSeverity === "803080001" || bSeverity === "803080002")
                return 1;
              else if (bSeverity === "803080003") return -1;
              else {
                if (aEffectiveDate < bEffectiveDate) return 1;
                else if (bEffectiveDate < aEffectiveDate) return -1;
                else return 0;
              }
            } else if (aSeverity === "803080003") {
              if (bSeverity !== "803080003") return 1;
              else {
                if (aEffectiveDate < bEffectiveDate) return 1;
                else if (bEffectiveDate < aEffectiveDate) return -1;
                else return 0;
              }
            }
          });

          setAllergies(sorted);
          // addToTimeline(
          //   response,
          //   "Allergies",
          //   "ccx_client_allergyid",
          //   "ccx_name",
          //   "ccx_effdate",
          //   "ccx_expdate",
          //   "createdby",
          //   "createdon"
          // );
        }
      });
      setUpdateAllergies(false);
    } else {
      setAllergies([]);
    }
  }, [clientId, updateAllergies, programsLoaded]);

  const handleUpdateAllergies = () => {
    setUpdateAllergies(true);
  };

  //Medications
  useEffect(() => {
    if ((clientId || updateMedications) && programsLoaded) {
      getMedications(clientId).then((response) => {
        if (response && isMountedRef.current) {
          setMedications(response);
          setUpdateMedications(false);
          // addToTimeline(
          //   response,
          //   "Medication",
          //   "ccx_clientmedicationid",
          //   "ccx_name",
          //   "ccx_startdate",
          //   "ccx_stopdate",
          //   "createdby",
          //   "createdon"
          // );
        }
      });
    } else {
      setMedications([]);
    }
  }, [clientId, updateMedications, programsLoaded]);

  const handleUpdateMedications = () => {
    setUpdateMedications(true);
  };

  //Get Client Payors
  useEffect(() => {
    if (!programsLoaded && !clientId && !updateClientPayors) {
      setClientPayors([]);
      return;
    }

    const loadClientPayors = async () => {
      let toastId;

      if (updateClientPayors) {
        toastId = toast.loading("Refreshing Client Payors...", {
          type: toast.TYPE.INFO,
        });
      }
      const response = await getClientPayors(clientId);

      if (!isMountedRef.current || !response) {
        return;
      }

      setClientPayors(response);
      setUpdateClientPayors(false);
      toast.dismiss(toastId);
    };

    loadClientPayors();
  }, [clientId, updateClientPayors, programsLoaded]);

  const handleUpdateClientPayors = () => {
    setUpdateClientPayors(true);
  };

  const clientPayorFundingChanges = useMemo(() => {
    return clientPayors
      .filter(
        (payor) =>
          payor.ccx_id2 !== undefined &&
          payor.ccx_expdate === undefined &&
          payor["payor.ccx_payorcode"].toUpperCase() === "NVMED" &&
          getFieldText(payor["payor.ccx_payortype"]) === "229580000"
      )
      .map((payor) => (
        <div className="col-2" key={payor.ccx_clientpayorid}>
          <div className="text-center text-white align-middle d-flex flex-fill align-items-center">
            <div className="w-100 rounded-pill bg-safy1-1 py-3">
              Funding Change:
              <br />
              {payor.ccx_id2}
            </div>
          </div>
        </div>
      ));
  }, [clientPayors]);

  const clientPayorNotes = useMemo(() => {
    if (!clientPayors || clientPayors.length === 0) {
      return <div className="col-4"></div>;
    }

    const payorNotes = clientPayors.filter(
      (payor) => !payor.ccx_expdate && payor.ccx_notes
    );

    if (payorNotes.length === 0) {
      return <div className="col-4"></div>;
    }

    return (
      <div className="col-4">
        {payorNotes.map((payor) => (
          <div
            key={payor.ccx_clientpayorid}
            className="border border-primary p-2 mb-2"
          >
            <b>{getFieldName(payor.ccx_payor)}</b>: {payor.ccx_notes}
          </div>
        ))}
      </div>
    );
  }, [clientPayors]);

  //Get Incidents
  // useEffect(() => {
  //   if (clientId) {
  //     getIncidents(selected["con.contactid"]).then((response) => {
  //       if (response && isMountedRef.current) {
  //         setIncidents(response);
  //         addToTimeline(
  //           response,
  //           "Incidents",
  //           "ccx_incidentreportid",
  //           "ccx_name",
  //           "ccx_dateandtimeofincident",
  //           "",
  //           "createdby",
  //           "createdon"
  //         );
  //       }
  //     });
  //   } else {
  //     setIncidents([]);
  //   }
  // }, [clientId]);

  //Get SOAR Correspondence
  // useEffect(() => {
  //   if (clientId) {
  //     getSoarCorrespondence(clientId).then((response) => {
  //       if (response && isMountedRef.current) {
  //         setCorrespondence(response);
  //         addToTimeline(
  //           response,
  //           "Correspondence",
  //           "sor_soarcorrespondanceid",
  //           "sor_name",
  //           "sor_dateandstarttime",
  //           "sor_dateandendtime",
  //           "createdby",
  //           "createdon"
  //         );
  //       }
  //     });
  //   } else {
  //     setCorrespondence([]);
  //   }
  // }, [clientId]);

  //Get Recorded Services
  // useEffect(() => {
  //   if (clientId && excludedServiceItemIds.length > 0) {
  //     getRecordedServices(clientId).then((response) => {
  //       if (response && isMountedRef.current) {
  //         setRecordedServices(response);
  //         const servicesExcludingBedDay = response.filter(
  //           (service) =>
  //             !excludedServiceItemIds.includes(
  //               getFieldText(service.ccx_serviceitem)
  //             )
  //         );
  //         addToTimeline(
  //           servicesExcludingBedDay,
  //           "Recorded Service",
  //           "ccx_recorded_serviceid",
  //           "ccx_name",
  //           "ccx_starttime",
  //           "ccx_endtime",
  //           "createdby",
  //           "createdon"
  //         );
  //       }
  //     });
  //   } else {
  //     setRecordedServices([]);
  //   }
  // }, [clientId, excludedServiceItemIds]);

  //Get Progress Notes
  useEffect(() => {
    if (clientId && programsLoaded) {
      getProgressNotes(clientId).then((response) => {
        if (response && isMountedRef.current) {
          setProgressNotes(response);
        }
      });
    } else {
      setProgressNotes([]);
    }
  }, [clientId, careTeamLoaded, programsLoaded]);

  //Get Encounters
  // useEffect(() => {
  //   if (clientId) {
  //     getEncounters(clientId).then((response) => {
  //       if (response && isMountedRef.current) {
  //         setEncounters(response);
  //         addToTimeline(
  //           response,
  //           "Encounter",
  //           "ccx_encounterid",
  //           "ccx_name",
  //           "ccx_starttime",
  //           "ccx_endtime",
  //           "createdby",
  //           "createdon"
  //         );
  //       }
  //     });
  //   } else {
  //     setEncounters([]);
  //   }
  // }, [clientId]);

  const handleShowExpired = (value) => {
    setShowExpired(value);
  };

  //Get Care Plans
  useEffect(() => {
    if (carePlans.length > 0 && !updateCarePlans) {
      return;
    }

    if (clientId && programsLoaded) {
      const loadClientRecords = async () => {
        const results = await Promise.all([
          getClientCarePlans(clientId),
          getReferralsByClient(clientId),
        ]);
        if (isMountedRef.current && results && results.length === 2) {
          const carePlanResults = results[0];
          const referralResults = results[1];

          if (carePlanResults) {
            setCarePlans(carePlanResults);
            setCurrentCarePlans(carePlanResults);
          }

          if (referralResults && referralResults.length > 0) {
            const acceptedReferrals = referralResults.filter(
              (referral) => referral.Status === "Accepted"
            );
            setReferrals(acceptedReferrals);
          }

          setUpdateCarePlans(false);
        }
      };

      loadClientRecords();
    } else {
      setCarePlans([]);
      setCurrentCarePlans([]);
    }
  }, [clientId, programsLoaded, updateCarePlans]);

  const handleUpdateCarePlans = () => {
    setUpdateCarePlans(true);
  };

  //Get Permanency Statuses
  useEffect(() => {
    if ((clientId || updatePermanencyStatuses) && programsLoaded) {
      getPermanencyStatuses(clientId).then((response) => {
        if (response && isMountedRef.current) {
          setPermanencyStatuses(response);
        }
      });
      setUpdatePermanencyStatuses(false);
    }
  }, [clientId, updatePermanencyStatuses, programsLoaded]);

  const handleUpdatePermanencyStatuses = () => {
    setUpdatePermanencyStatuses(true);
  };

  //Get Bed Appointments
  useEffect(() => {
    if ((clientId || updateBedAppointments) && programsLoaded) {
      getBedAppointments(clientId).then((response) => {
        if (response && isMountedRef.current) {
          setBedAppointments(response);
        }
      });

      setUpdateBedAppointments(false);
    }
  }, [clientId, updateBedAppointments, programsLoaded]);

  const handleUpdateBedAppointments = () => {
    setUpdateBedAppointments(true);
  };

  //Get Client Identifiers
  useEffect(() => {
    if ((clientId || updateClientIdentifiers) && programsLoaded) {
      getClientIdentifiers(clientId).then((response) => {
        if (response && isMountedRef.current) {
          setClientIdentifiers(response);
        }
      });

      setUpdateClientIdentifiers(false);
    }
  }, [clientId, updateClientIdentifiers, programsLoaded]);

  const handleUpdateClientIdentifiers = () => {
    setUpdateClientIdentifiers(true);
  };

  const loadClientProgramIdentifiers = async (clientProgram) => {
    const response = await getClientProgramIdentifiers(
      clientProgram,
      safyInfo.user.safyInfo.BusinessUnitIdName
    );

    if (!isMountedRef.current || !response) {
      return;
    }

    setClientProgramIdentifiers(response);
  };

  // const name =
  //   contact["firstname"] +
  //   (contact["con.middlename"] ? " " + contact["middlename"] : "") +
  //   (contact["lastname"] ? " " + contact["lastname"] : "");

  // const preferredName =
  //   (contact["ccx_alias1"] ? contact["ccx_alias1"] : contact["firstname"]) +
  //   (contact["ccx_alias2"]
  //     ? " " + contact["ccx_alias2"]
  //     : contact["lastname"]
  //     ? " " + contact["lastname"]
  //     : "");

  // const getRace = () => {
  //   let races = [];
  //   if (getFieldName(contact["sor_americanindianoralaskanative"]) === "Yes")
  //     races.push("American Indian or Alaska Native");
  //   if (
  //     getFieldName(contact["sor_nativehawaiianorotherpacificislander"]) ===
  //     "Yes"
  //   )
  //     races.push("Native Hawaiian or Other Pacific Islander");
  //   if (getFieldName(contact["sor_blackorafricanamerican"]) === "Yes")
  //     races.push("Black or African American");
  //   if (getFieldName(contact["sor_white"]) === "Yes") races.push("White");
  //   if (getFieldName(contact["sor_asian"]) === "Yes") races.push("Asian");
  //   if (races.length === 0) return "Not Identified";
  //   if (races.length === 1) return races[0];
  //   return races.join(", ");
  // };

  const openContact = () => {
    const url =
      process.env.REACT_APP_ORG +
      "/main.aspx?etc=2&extraqs=formid%3ddce13c2e-cc2a-4081-9921-df8dfaefcfac&id=" +
      clientId +
      "&pagetype=entityrecord";
    window.open(url, "contact");
  };

  const loadTimeline = () => {
    if (clientId) {
      Promise.all([
        getRecordedServices(clientId),
        getEncounters(clientId),
        getSoarCorrespondence(clientId),
        getIncidents(clientId),
      ])
        .then((response) => {
          if (isMountedRef.current) {
            console.group(`Processing Timeline records for ${clientId}`);
            const recordedServicesResponse = response[0];
            const encountersResponse = response[1];
            const correspondenceResponse = response[2];
            const incidentsResponse = response[3];

            //Recorded Services
            if (recordedServicesResponse) {
              setRecordedServices(recordedServicesResponse);
              const servicesExcludingBedDay = recordedServicesResponse.filter(
                (service) =>
                  !excludedServiceItemIds.includes(
                    getFieldText(service.ccx_serviceitem)
                  )
              );
              addToTimeline(
                servicesExcludingBedDay,
                "Recorded Service",
                "ccx_recorded_serviceid",
                "ccx_name",
                "ccx_starttime",
                "ccx_endtime",
                "createdby",
                "createdon",
                "ccx_clientprogram"
              );
            }

            //Encounters
            if (encountersResponse) {
              setEncounters(encountersResponse);
              const unsignedEncounters = encountersResponse.filter(
                (e) => getFieldName(e.ccx_esignature) === "No"
              );
              addToTimeline(
                unsignedEncounters,
                "Encounter",
                "ccx_encounterid",
                "ccx_name",
                "ccx_starttime",
                "ccx_endtime",
                "createdby",
                "createdon",
                "ccx_clientprogram"
              );
            }

            //Correspondence
            if (correspondenceResponse) {
              setCorrespondence(correspondenceResponse);
              addToTimeline(
                correspondenceResponse,
                "Correspondence",
                "sor_soarcorrespondanceid",
                "sor_name",
                "sor_dateandstarttime",
                "sor_dateandendtime",
                "createdby",
                "createdon",
                ""
              );

              //Incidents
              if (incidentsResponse) {
                setIncidents(incidentsResponse);
                addToTimeline(
                  incidentsResponse,
                  "Incidents",
                  "ccx_incidentreportid",
                  "ccx_name",
                  "ccx_dateandtimeofincident",
                  "",
                  "createdby",
                  "createdon",
                  "sor_clientprogram"
                );
              }
            }
          }
        })
        .finally(() => {
          console.groupEnd();
          setTimelineLoaded(true);
        });
    } else {
      setRecordedServices([]);
      setEncounters([]);
      setCorrespondence([]);
      setIncidents([]);
    }
  };

  useEffect(() => {
    if (programs.length === 0 || programsLoaded) {
      return;
    }

    const mobileCrisisPrograms = programs.filter((p) =>
      p.ccx_name.includes("Mobile Crisis Response")
    );

    if (mobileCrisisPrograms.length === 0) {
      setProgramsLoaded(true);
      return;
    }

    let ignore = false;
    const getCrisisReferrals = async () => {
      const body = {
        command: "query",
        application:
          process.env.REACT_APP_ENVIRONMENT === "Production"
            ? "5DAE139E-D052-4BD8-BF4A-499A31AC3C15"
            : "58990F83-8566-4BA8-B4B5-584A64E4CBA1",
        keyword: "byclientprogram",
        ids: mobileCrisisPrograms.map((p) => p.ccx_client_programid),
      };

      const response = await crisisReferrals(body);

      if (!ignore) {
        if (response.data?.length > 0) {
          setPrograms((p) => {
            const programsArr = [...p];

            programsArr.forEach((program) => {
              if (program.ccx_name.includes("Mobile Crisis Response")) {
                program.crisisReferral = response.data.find(
                  (cr) =>
                    `{${cr.clientProgram}}` === program.ccx_client_programid
                );
              }
            });

            return programsArr;
          });
        }

        setProgramsLoaded(true);
      }
    };

    getCrisisReferrals();

    return () => (ignore = true);
  }, [programs, programsLoaded]);

  useEffect(() => {
    let admitDate = DateTime.fromISO("2025-04-01");

    if (selected?.ccx_admitdate) {
      admitDate = DateTime.fromISO(getFieldText(selected.ccx_admitdate));
    }

    const crisisReferralLaunchDate = DateTime.fromISO("2025-04-01");

    if (
      admitDate >= crisisReferralLaunchDate &&
      programsLoaded &&
      selected?.ccx_name.includes("Mobile Crisis Response") &&
      !selected?.crisisReferral
    ) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <div class="react-confirm-alert-body">
            <h5>
              <IoIosWarning
                title="Missing Crisis Referral"
                size="1.25em"
                className="me-2 text-danger"
              />
              Missing Crisis Referral
            </h5>
            <div>
              No Mobile Crisis Referral associated with this Program. Select the
              appropriate Program to proceed.
              <br />
              <br />
              Support has been notified.
            </div>
            <div className="d-flex justify-content-end">
              <div class="react-confirm-alert-button-group mt-2">
                <button label="Close" onClick={onClose}>
                  Close
                </button>
              </div>
            </div>
          </div>
        ),
        closeOnClickOutside: false,
      });

      if (
        !safyInfo.user.modules.includes("Admin") &&
        process.env.REACT_APP_ENVIRONMENT === "Production"
      ) {
        sendEmail(
          "support@safy.org",
          safyInfo.user.email,
          "SAFY Portal - Duplicate Crisis Referral",
          `<b>${
            safyInfo.user.displayName
          }</b> attempted to access a duplicate Mobile Crisis Response program for client <b>${getFieldName(
            selected.ccx_patient
          )}</b>.<br/><br/>Click <a href="https://safyportal.safy.org/client/${getFieldText(
            selected.ccx_patient
          ).replace(
            /[{}]/g,
            ""
          )}">https://safyportal.safy.org/client/${getFieldText(
            selected.ccx_patient
          ).replace(
            /[{}]/g,
            ""
          )}</a> to access the Client in the Portal.<br/><br/>Click <a href="https://ccplive.safy.org/main.aspx?etc=10084&extraqs=id=${
            selected.ccx_client_programid
          }&pagetype=entityrecord">https://ccplive.safy.org/main.aspx?etc=10084&extraqs=id=${
            selected.ccx_client_programid
          }&pagetype=entityrecord</a> to open the Client Program in SOAR.<br/><br/>`
        );
      }
    }
  }, [programsLoaded, selected]);

  const selectedIsMissingReferral = useMemo(() => {
    let admitDate;

    if (selected?.ccx_admitdate) {
      admitDate = DateTime.fromISO(getFieldText(selected.ccx_admitdate));
    }

    const crisisReferralLaunchDate = DateTime.fromISO("2025-04-01");

    return (
      admitDate >= crisisReferralLaunchDate &&
      selected?.ccx_name.includes("Mobile Crisis Response") &&
      selected?.crisisReferral === undefined
    );
  }, [selected?.ccx_admitdate, selected?.ccx_name, selected?.crisisReferral]);

  return (
    <>
      {!contactLoaded || !programsLoaded ? (
        <>
          <div className="d-flex justify-content-center align-items-center">
            <h1>LOADING CLIENT INFO</h1>
            <Lottie
              animationData={contactLoading.default}
              loop={true}
              autoplay={true}
              rendererSettings={{
                preserveAspectRatio: "xMidYMid slice",
              }}
              height={120}
              width={120}
            />
          </div>
        </>
      ) : null}
      {contactLoaded && programsLoaded ? (
        <>
          <BackIcon />
          {debugMode ? (
            <Button
              onClick={() => {
                console.group("Everything");
                console.log("SAFY Info", safyInfo);
                console.log("Initial Tab", initialTab);
                console.log("All Programs", allPrograms);
                console.log("Programs", programs);
                console.log("Programs Loaded", programsLoaded);
                console.log("Current Care Plans", currentCarePlans);
                console.log("Selected", selected);
                console.log("Contact", contact);
                console.log("Diagnosis", diagnosis);
                console.log("Care Team", careTeam);
                console.log("Care Plans", carePlans);
                console.log("Recorded Services", recordedServices);
                console.log("Progress Notes", progressNotes);
                console.log("Encounters", encounters);
                console.log("Correspondence", correspondence);
                console.log("Incidents", incidents);
                console.log("Client Payors", clientPayors);
                console.log("Address", addresses);
                console.log("Beds", beds);
                console.log("Modalities", modalities);
                console.log("Rates", rates);
                console.log("Allergies", allergies);
                console.log("Medications", medications);
                console.log("Permanency Statues", permanencyStatuses);
                console.log("Bed Appointments", bedAppointments);
                console.log("Client Identifiers", clientIdentifiers);
                console.log("Foster Home Programs", fosterHomePrograms);
                console.log("Referrals", referrals);
                console.log("Timeline", timeline);
                console.groupEnd();
              }}
            >
              Log to Console
            </Button>
          ) : null}
          {/* <PrintIcon /> */}
          <div className="row mt-2">
            <div className="col">
              {clientPhoto ? (
                <img
                  className="float-start mx-4"
                  src={clientPhoto}
                  style={{ width: "90px", height: "90px" }}
                  onClick={openContact}
                ></img>
              ) : (
                <i
                  className="far fa-user-circle fa-7x float-start mx-4"
                  style={{ width: "90px", height: "90px" }}
                  onClick={openContact}
                ></i>
              )}
              <div className="d-flex">
                <div
                  className={`h3 mb-0 me-2${
                    contact.sor_clientflags?.includes("chartHolder")
                      ? " bg-safy-secondary2"
                      : ""
                  }`}
                  title="Family Chart Holder"
                >
                  {`${contact.lastname}, ${contact.firstname}-${contact.ccx_mpistr}`}
                </div>
                {contact.sor_clientflags?.includes("medFrag") ? (
                  <img
                    src={medFragIcon}
                    title="Medically Fragile"
                    alt="Medically Fragile icon"
                    style={{ height: "2.375rem" }}
                  ></img>
                ) : null}
              </div>
              <div className="h6 mt-0">
                {getFieldName(contact.gendercode)} DOB:{" "}
                {getFieldDate(contact.birthdate)}
                {contact.birthdate &&
                  " Age: " +
                    getAge(
                      getFieldDate(contact.birthdate),
                      contact.ccx_deceaseddate
                    )}
              </div>
              {selected !== undefined && !selectedIsMissingReferral ? (
                <>
                  <ButtonGroup>
                    {/* <SOARContactButton
              className="me-1 mb-2 bg-safy3-1"
              size="sm"
              contactID={clientId}
            /> */}
                    <EncounterButton
                      className="me-1 mb-2 bg-safy3-1"
                      size="sm"
                      contactID={clientId}
                    />
                    {allPrograms.filter(
                      (p) =>
                        p.ccx_name !==
                        "SAFY of Colorado - Aurora - Mobile Crisis Response"
                    ).length === allPrograms.length && (
                      <CarePlanButton
                        className="me-1 mb-2 bg-safy3-1"
                        size="sm"
                        contactID={clientId}
                      />
                    )}
                    {!getFieldName(contact.owner)?.includes("South Carolina") &&
                    !getFieldName(contact.owner)?.includes("Indiana") &&
                    moment().isBefore(moment("2024-08-21")) ? (
                      <IncidentReportButton
                        className="me-1 mb-2 bg-safy3-1"
                        size="sm"
                        contactID={clientId}
                        title="IR"
                      />
                    ) : null}
                    {safyInfo.user.modules.includes("Admin") ||
                    safyInfo.user.modules.includes("Rev Cycle") ? (
                      <RecordedServiceButton
                        className="me-1 mb-2 bg-safy3-1"
                        size="sm"
                        contactID={clientId}
                        title="Recorded Service"
                        selected={selected}
                      />
                    ) : null}
                  </ButtonGroup>
                </>
              ) : null}
            </div>
            {clientPayorFundingChanges}
            {clientPayorNotes}
          </div>
          {selected["con.sor_risklevel"] &&
          selected["con.sor_risklevel"]["@name"] === "High" ? (
            <div className="rounded bg-danger text-white p-2 m-4 fw-bold">
              High Risk
            </div>
          ) : null}
          <div className="row mb-4 mt-4">
            <div className="row col">
              {selected &&
                programs &&
                programs
                  .filter(
                    (program) =>
                      showExpired || program.ccx_dischargedate === undefined
                  )
                  .map((program) => (
                    <div
                      className="col-sm-6 p-2"
                      key={program.ccx_client_programid}
                    >
                      <ProgramRadioSelect
                        program={program}
                        careplan={
                          currentCarePlans.length > 0 &&
                          currentCarePlans.filter(
                            (cp) =>
                              getFieldText(cp.ccx_clientprogram) ===
                              program.ccx_client_programid
                          )
                        }
                        selected={selected}
                        history={
                          formHistory &&
                          formHistory.filter((h) =>
                            clientId.includes(h.ContactId)
                          )
                        }
                        onChange={() => {
                          setSelected(program);
                          loadClientProgramIdentifiers(
                            program.ccx_client_programid
                          );
                        }}
                      />
                    </div>
                  ))}
            </div>
            <div className="col-md-3 mt-4">
              {programs.length > 0 ? (
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="client2Expired"
                    checked={showExpired}
                    onChange={(e) => handleShowExpired(e.currentTarget.checked)}
                  />
                  <label className="form-check-label" htmlFor="client2Expired">
                    Show Expired Records
                  </label>
                </div>
              ) : null}
            </div>
          </div>
          {!selectedIsMissingReferral ? (
            <div className="w-100">
              <Tabs
                activeKey={activeTab}
                id="clientprogram-tabs"
                className="cptabs mb-4"
                transition={false}
                onSelect={(key) => {
                  setActiveTab(key);

                  if (key === "clinicalHistory" && !timelineLoaded)
                    loadTimeline();
                }}
              >
                {selected !== undefined ? (
                  <Tab eventKey="client" title="Face Sheet" className="cptabs">
                    {activeTab === "client" ? (
                      <ClientInfo
                        clientProgram={selected}
                        fosterHomePrograms={fosterHomePrograms}
                        addresses={addresses}
                        updateAddresses={handleUpdateAddresses}
                        clientPayors={clientPayors}
                        updateClientPayors={handleUpdateClientPayors}
                        beds={beds}
                        updateBeds={handleUpdateBeds}
                        modalities={modalities}
                        updateModalities={handleUpdateModalities}
                        rates={rates}
                        updateRates={handleUpdateRates}
                        allergies={allergies}
                        updateAllergies={handleUpdateAllergies}
                        medications={medications}
                        updateMedications={handleUpdateMedications}
                        permanencyStatuses={permanencyStatuses}
                        updatePermanencyStatuses={
                          handleUpdatePermanencyStatuses
                        }
                        bedAppointments={bedAppointments}
                        updateBedAppointments={handleUpdateBedAppointments}
                        recordedServices={recordedServices}
                        showExpired={showExpired}
                        updateCareTeam={handleUpdateCareTeam}
                        existingCareTeam={careTeam}
                        clientIdentifiers={clientIdentifiers}
                        updateClientIdentifiers={handleUpdateClientIdentifiers}
                        clientProgramIdentifiers={clientProgramIdentifiers}
                        loadClientProgramIdentifiers={
                          loadClientProgramIdentifiers
                        }
                        referrals={referrals}
                        contact={contact}
                        updateDiagnosis={handleUpdateDiagnosis}
                      />
                    ) : null}
                  </Tab>
                ) : null}
                {selected !== undefined ? (
                  <Tab
                    eventKey="diagnosis"
                    title="Diagnosis"
                    className="cptabs"
                  >
                    {activeTab === "diagnosis" ? (
                      <DiagnosisEntity
                        clientId={clientId}
                        diagnosis={diagnosis}
                        selectedProgram={selected}
                        showExpired={showExpired}
                        updateDiagnosis={handleUpdateDiagnosis}
                      />
                    ) : null}
                  </Tab>
                ) : null}
                {selected !== undefined ? (
                  <Tab eventKey="team" title="Care Team" className="cptabs">
                    {activeTab === "team" ? (
                      <CareTeamEntity
                        selectedProgram={selected}
                        careTeam={careTeam}
                        showExpired={showExpired}
                        updateCareTeam={handleUpdateCareTeam}
                      ></CareTeamEntity>
                    ) : null}
                  </Tab>
                ) : null}
                {/* <Tab eventKey="reports" title="Reports" className="cptabs">
            <h3>Reports</h3>
          </Tab> */}
                {selected !== undefined ? (
                  <Tab
                    eventKey="clinicalHistory"
                    title="Clinical History"
                    className="cptabs"
                  >
                    {activeTab === "clinicalHistory" ? (
                      <>
                        {!timelineLoaded && (
                          <>
                            <div className="d-flex justify-content-center align-items-center">
                              <h1>LOADING CLINICAL HISTORY TIMELINE</h1>
                              <Lottie
                                animationData={contactLoading.default}
                                loop={true}
                                autoplay={true}
                                rendererSettings={{
                                  preserveAspectRatio: "xMidYMid slice",
                                }}
                                height={120}
                                width={120}
                              />
                            </div>
                          </>
                        )}
                        {timelineLoaded && (
                          <DocumentationTimeline
                            timeline={timeline}
                            careTeam={careTeam}
                            diagnosis={diagnosis}
                            incidents={incidents}
                            correspondence={correspondence}
                            recordedServices={recordedServices}
                            progressNotes={progressNotes}
                            encounters={encounters}
                            clientID={clientId}
                            contactName={`${contact.lastname}, ${contact.firstname}`}
                            selectedProgram={selected}
                            contact={contact}
                          />
                        )}
                      </>
                    ) : null}
                  </Tab>
                ) : null}
                {selected &&
                !selected.ccx_name.includes("Mobile Crisis Response") ? (
                  <Tab
                    eventKey="cpass"
                    title="Assessments & Care Plan"
                    className="cptabs"
                  >
                    {activeTab === "cpass" ? (
                      <Assessments
                        contactID={clientId}
                        carePlans={
                          carePlans?.length > 0
                            ? carePlans.filter(
                                (cp) =>
                                  getFieldText(cp.ccx_clientprogram) ===
                                  selected.ccx_client_programid
                              )
                            : []
                        }
                        updateCarePlans={handleUpdateCarePlans}
                        clientProgram={selected}
                        clientIdentifiers={clientIdentifiers}
                      />
                    ) : null}
                  </Tab>
                ) : null}
                {selected?.["unit.sor_safyportalconfig"] !== undefined ? (
                  <Tab eventKey="forms" title="Forms" className="cptabs">
                    {activeTab === "forms" ? (
                      <FormsTab
                        contactID={clientId}
                        clientProgram={selected}
                        careTeam={careTeam}
                      ></FormsTab>
                    ) : null}
                  </Tab>
                ) : null}
                {safyInfo.user.modules.includes("Admin") &&
                selected !== undefined ? (
                  <Tab
                    eventKey="bedDays"
                    title="Census Days"
                    className="cptabs"
                  >
                    {activeTab === "bedDays" ? (
                      <BedDaysTab
                        contactID={clientId}
                        clientProgram={selected}
                        modalities={modalities}
                        payors={clientPayors}
                        birthdate={contact.birthdate}
                      ></BedDaysTab>
                    ) : null}
                  </Tab>
                ) : null}
                <Tab eventKey="documents" title="Documents" className="cptabs">
                  {activeTab === "documents" ? (
                    <Documents
                      id={clientId}
                      type="ClientID"
                      state={allPrograms[0]?.ccx_company["@name"]?.replace(
                        "SAFY of ",
                        ""
                      )}
                      puconfig={
                        selected["unit.sor_safyportalconfig"]
                          ? JSON.parse(
                              selected["unit.sor_safyportalconfig"] || {}
                            )
                          : {}
                      }
                      prevLocation={contact?.doclocation[0]}
                    />
                  ) : null}
                </Tab>
              </Tabs>
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
};

export default Client2Wrapper;
